import { useState, useEffect } from 'react';
import { WifiOff, Wifi, CloudOff, RefreshCw, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(true);
      toast.success('You are back online!', {
        description: pendingCount > 0 ? 'Syncing pending reports...' : 'Connection restored.',
        duration: 3000
      });
      
      // Auto-sync pending reports
      if (pendingCount > 0) {
        syncPendingReports();
      }
      
      // Hide banner after 5 seconds
      setTimeout(() => setShowBanner(false), 5000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
      toast.warning('You are offline', {
        description: 'Some features may be limited. Reports will be saved locally.',
        duration: 5000
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check pending count periodically
    const checkPending = () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          setPendingCount(event.data.count || 0);
        };
        navigator.serviceWorker.controller.postMessage(
          { type: 'GET_PENDING_COUNT' },
          [messageChannel.port2]
        );
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 30000);

    // Listen for precache complete
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'PRECACHE_COMPLETE') {
          toast.success('Offline data ready', {
            description: 'You can now use the app offline.',
            duration: 3000
          });
        }
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [pendingCount]);

  const syncPendingReports = async () => {
    if (!navigator.serviceWorker.controller) return;
    
    setSyncing(true);
    navigator.serviceWorker.controller.postMessage({ type: 'SYNC_NOW' });
    
    // Wait a bit and check pending count
    setTimeout(() => {
      setSyncing(false);
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        const newCount = event.data.count || 0;
        setPendingCount(newCount);
        if (newCount === 0) {
          toast.success('All reports synced!');
        }
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_PENDING_COUNT' },
        [messageChannel.port2]
      );
    }, 3000);
  };

  // Don't show anything if online and no pending
  if (isOnline && pendingCount === 0 && !showBanner) {
    return null;
  }

  return (
    <>
      {/* Floating offline indicator */}
      {!isOnline && (
        <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-amber-600 text-white shadow-lg animate-pulse">
          <WifiOff className="w-4 h-4" />
          <span className="text-sm font-medium">Offline Mode</span>
        </div>
      )}

      {/* Pending reports indicator */}
      {pendingCount > 0 && (
        <div className={cn(
          "fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg",
          isOnline ? "bg-sky-600 text-white" : "bg-slate-800 text-slate-200 border border-slate-700"
        )}>
          <CloudOff className="w-5 h-5" />
          <div>
            <p className="text-sm font-medium">{pendingCount} pending report{pendingCount > 1 ? 's' : ''}</p>
            <p className="text-xs opacity-80">
              {isOnline ? 'Ready to sync' : 'Will sync when online'}
            </p>
          </div>
          {isOnline && (
            <Button
              size="sm"
              variant="secondary"
              onClick={syncPendingReports}
              disabled={syncing}
              className="ml-2 bg-white/20 hover:bg-white/30 text-white"
            >
              {syncing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                'Sync Now'
              )}
            </Button>
          )}
        </div>
      )}

      {/* Connection restored banner */}
      {showBanner && isOnline && pendingCount === 0 && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white shadow-lg animate-in slide-in-from-top duration-300">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Connection restored</span>
        </div>
      )}
    </>
  );
}

// Hook for components to check offline status
export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, isOffline: !isOnline };
}

// Function to trigger pre-cache after login
export function precacheOfflineData(token) {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'PRECACHE_DATA',
      token
    });
  }
}
